import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Theme } from '@mui/material';

type ThemeMode = 'light' | 'dark';

interface ThemeModeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(undefined);

export const useThemeMode = (): ThemeModeContextType => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};

export const getAppTheme = (mode: ThemeMode): Theme => {
  return createTheme({
    spacing: (factor: number) => {
      if (factor === 3) return 10;
      return factor * 8;
    },
    typography: {
      fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    palette: {
      mode,
      primary: {
        main: mode === 'light' ? '#1f3dff' : '#4f66ff',
        light: mode === 'light' ? '#7f8ffd' : '#2a3b90',
        dark: mode === 'light' ? '#0318a1' : '#002596',
      },
      background: {
        default: mode === 'light' ? '#f1f3ff' : '#090d16',
        paper: mode === 'light' ? '#ffffff' : '#111827',
      },
      text: {
        primary: mode === 'light' ? '#333333' : '#f3f4f6',
        secondary: mode === 'light' ? '#666666' : '#9ca3af',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  });
};

interface ThemeModeProviderProps {
  children: React.ReactNode;
}

export const ThemeModeProvider: React.FC<ThemeModeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('wday-theme-mode');
    return savedMode === 'dark' || savedMode === 'light' ? savedMode : 'light';
  });

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  useEffect(() => {
    localStorage.setItem('wday-theme-mode', mode);
    const rootElement = window.document.documentElement;
    if (mode === 'dark') {
      rootElement.classList.add('dark');
    } else {
      rootElement.classList.remove('dark');
    }
  }, [mode]);

  const theme = getAppTheme(mode);

  return (
    <ThemeModeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
